/* eslint-disable react/prop-types */
import { type CSSProperties, forwardRef, type ReactNode } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

export type CursorColor = 'yellow' | 'blue';

export type JsmPlaygroundCursorProps = {
	label?: ReactNode;
	color?: CursorColor;
	isActive?: boolean;
	isClicking?: boolean;
	isHovering?: boolean;
	style?: CSSProperties;
	className?: string;
	dataCustomCursor?: string;
};

const getCursorId = (dataCustomCursor?: string, label?: ReactNode): string => {
	if (dataCustomCursor) {
		return dataCustomCursor;
	}
	return typeof label === 'string' ? label.toLowerCase() : 'custom';
};

export const JsmPlaygroundCursor = forwardRef<HTMLDivElement, JsmPlaygroundCursorProps>(
	(props, ref) => {
		const {
			label = 'You',
			color = 'yellow',
			isActive = false,
			isClicking = false,
			isHovering = false,
			style,
			dataCustomCursor,
		} = props;

		const isBlue = color === 'blue';
		const cursorId = getCursorId(dataCustomCursor, label);

		return (
			<div
				ref={ref}
				aria-hidden="true"
				data-custom-cursor={cursorId}
				data-cursor-color={color}
				data-cursor-active={String(isActive)}
				data-cursor-hovering={String(isHovering)}
				data-cursor-clicking={String(isClicking)}
				style={style}
				css={[customCursorStyles, isActive && customCursorVisibleStyles]}
			>
				<div css={cursorBodyStyles}>
					<svg
						css={[
							customCursorPointerStyles,
							isBlue && pointerBlueStyles,
							isHovering && pointerHoveringStyles,
							isClicking && pointerClickingStyles,
						]}
						width="24"
						height="48"
						viewBox="0 0 23 23"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						aria-hidden="true"
					>
						<path
							d="M7.73535 22.6201L0.000042 0.000059L22.5846 7.77092L12.6866 12.7218L7.73535 22.6201Z"
							fill="currentColor"
						/>
					</svg>
					<div
						css={[
							customCursorLabelStyles,
							isBlue && labelBlueStyles,
							isHovering && labelHoveringStyles,
							isClicking && labelClickingStyles,
						]}
					>
						{label}
					</div>
				</div>
			</div>
		);
	},
);

JsmPlaygroundCursor.displayName = 'JsmPlaygroundCursor';

export default JsmPlaygroundCursor;

const customCursorStyles = css({
	position: 'absolute',
	insetBlockStart: 0,
	insetInlineStart: 0,
	zIndex: 5,
	width: 0,
	height: 0,
	opacity: 0,
	pointerEvents: 'none',
	willChange: 'transform',
	transform: 'translate3d(var(--cursor-x, 0px), var(--cursor-y, 0px), 0)',
	transition: 'opacity 120ms ease',
	'@media (pointer: coarse)': {
		display: 'none',
	},
});

const customCursorVisibleStyles = css({
	opacity: 1,
});

const cursorBodyStyles = css({
	position: 'relative',
	width: 0,
	height: 0,
});

const customCursorLabelStyles = css({
	position: 'absolute',
	insetBlockStart: '14px',
	insetInlineStart: '22px',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	paddingBlock: token('space.050'),
	paddingInline: token('space.150'),
	borderRadius: token('radius.full'),
	backgroundColor: token('color.background.warning.bold'),
	color: token('color.text'),
	font: token('font.heading.medium'),
	fontWeight: token('font.weight.semibold'),
	whiteSpace: 'nowrap',
	boxShadow: token('elevation.shadow.overlay'),
	transition:
		'transform 250ms cubic-bezier(0.22, 1, 0.36, 1), background-color 150ms ease, color 150ms ease',
});

const labelBlueStyles = css({
	backgroundColor: token('color.background.brand.bold'),
	color: token('color.text.inverse'),
});

const labelHoveringStyles = css({
	transform: 'translate(4px, 8px)',
});

const labelClickingStyles = css({
	transform: 'translate(-2px, -3px)',
});

const customCursorPointerStyles = css({
	position: 'absolute',
	insetBlockStart: '-16px',
	insetInlineStart: 0,
	width: '24px',
	height: '48px',
	transform: 'scale(1)',
	transformOrigin: '0 0',
	color: token('color.background.warning.bold'),
	transition:
		'transform 250ms cubic-bezier(0.22, 1, 0.36, 1), filter 250ms cubic-bezier(0.22, 1, 0.36, 1), color 150ms ease',
});

const pointerBlueStyles = css({
	color: token('color.background.brand.bold'),
});

const pointerHoveringStyles = css({
	transform: 'scale(1.3)',
	// eslint-disable-next-line local/ensure-wac-token-usage
	filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.25))',
});

const pointerClickingStyles = css({
	transform: 'scale(0.92)',
	filter: 'none',
});
